import { useState, useEffect, useCallback } from "react";
import { useMsal } from "@azure/msal-react";
import { loginScopes } from "../auth/msalConfig";
import { sqlRead } from "../utils/sqlClient";
// Room-parametric math lives in the shared util (also used by the per-revision
// Room Takeoff module) — this page keeps only its spine/weight aggregation.
import {
  MATERIAL_LABELS, ROOM_TYPES, AHU_TYPES,
  roundDia, lbsPerFt, calcRoom, DEFAULT_DUCT_TUNABLES,
} from "../utils/takeoffMath";

// ── Helpers ───────────────────────────────────────────────────────────────────
const uid  = () => Math.random().toString(36).slice(2, 9);
const fmt  = (n, d = 0) => isNaN(n) ? "—" : Number(n).toLocaleString("en-US", { maximumFractionDigits: d, minimumFractionDigits: d });
function calcAhu(ahu, tunables) {
  const rooms = ahu.rooms.map(r => ({ ...r, ...calcRoom(r, tunables) }));
  const totalCFM = rooms.reduce((s, r) => s + r.cfm, 0);
  let remaining  = totalCFM;
  const spineSegments = rooms.map((r, i) => {
    const start = i === 0 ? 0 : rooms[i - 1].spinePosition;
    const len   = Math.max(0, r.spinePosition - start);
    const dia   = roundDia(remaining, tunables.TRUNK_FPM);
    const seg   = { cfm: remaining, dia, length: len };
    remaining  -= r.cfm;
    return seg;
  });
  return { rooms, totalCFM, spineSegments };
}
function calcWeights(ahu, ahuCalc, isOA, tunables) {
  const ff  = 1 + ahu.fittingsPct / 100;
  const mat = ahu.material;
  let supplySpineLbs = 0, supplyBranchLbs = 0, returnRoomLbs = 0, exhaustRoomLbs = 0;
  for (const seg of ahuCalc.spineSegments)
    supplySpineLbs += seg.length * ff * lbsPerFt(seg.dia, mat);
  for (const r of ahuCalc.rooms) {
    supplyBranchLbs += r.branchLength * r.hepaCount * ff * lbsPerFt(r.branchDia, mat);
    returnRoomLbs   += r.returnLength  * ff * lbsPerFt(r.returnDia,  mat);
    if (isOA) exhaustRoomLbs += r.exhaustLength * ff * lbsPerFt(r.exhaustDia, mat);
  }
  const retSpineDia = roundDia(ahuCalc.totalCFM, tunables.RETURN_FPM);
  const retSpineLbs = ahu.returnSpineLength * ff * lbsPerFt(retSpineDia, mat);
  const exSpineDia  = roundDia(ahuCalc.totalCFM, tunables.EXHAUST_FPM);
  const exSpineLbs  = isOA ? ahu.exhaustSpineLength * ff * lbsPerFt(exSpineDia, mat) : 0;
  const totalSupply  = supplySpineLbs + supplyBranchLbs;
  const totalReturn  = returnRoomLbs + retSpineLbs;
  const totalExhaust = exhaustRoomLbs + exSpineLbs;
  const grandTotal   = totalSupply + totalReturn + totalExhaust;
  return { supplySpineLbs, supplyBranchLbs, returnRoomLbs, retSpineLbs, retSpineDia, exhaustRoomLbs, exSpineLbs, exSpineDia, totalSupply, totalReturn, totalExhaust, grandTotal };
}

// ── Defaults ──────────────────────────────────────────────────────────────────
const defaultRoom = (pos = 20) => ({
  id: uid(), name: "New Room", type: "iso7",
  length: 20, width: 15, height: 10, ach: 45,
  spinePosition: pos, branchLength: 8, returnLength: 20, exhaustLength: 30,
});
const defaultAhu = () => ({
  id: uid(), name: "AHU-1", type: "recirculating",
  material: "galvanized", fittingsPct: 25,
  returnSpineLength: 60, exhaustSpineLength: 80,
  rooms: [defaultRoom(20), { ...defaultRoom(50), name: "Room 2", id: uid() }],
});
const defaultProject = () => ({ name: "New Facility Estimate", ahus: [defaultAhu()] });

// ── Primitives ────────────────────────────────────────────────────────────────
function NumField({ label, value, onChange, min = 0, max, step = 1, unit, hint }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 3, minWidth: 90 }}>
      <label style={{ fontSize: 11, color: "var(--text2)", fontWeight: 500 }}>
        {label}{unit && <span style={{ color: "var(--text3)" }}> {unit}</span>}
      </label>
      {hint && <div style={{ fontSize: 10, color: "var(--text3)", fontFamily: "monospace" }}>{hint}</div>}
      <input type="number" value={value} min={min} max={max} step={step}
        onChange={e => onChange(Number(e.target.value))}
        style={{ width: "100%", background: "var(--surface)", border: "1px solid var(--cs-border2)", borderRadius: "var(--radius)", color: "var(--text)", fontSize: 13, fontWeight: 600, padding: "6px 8px", outline: "none" }} />
    </div>
  );
}
function SelField({ label, value, onChange, options }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 3, minWidth: 120 }}>
      <label style={{ fontSize: 11, color: "var(--text2)", fontWeight: 500 }}>{label}</label>
      <select value={value} onChange={e => onChange(e.target.value)}
        style={{ background: "var(--surface)", border: "1px solid var(--cs-border2)", borderRadius: "var(--radius)", color: "var(--text)", fontSize: 13, padding: "6px 8px", outline: "none" }}>
        {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </div>
  );
}
function ResultRow({ label, value, sub, accent, indent }) {
  return (
    <div style={{
      display: "flex", justifyContent: "space-between", alignItems: "baseline",
      borderBottom: accent ? "none" : "1px solid var(--cs-border)",
      paddingLeft: indent ? 12 : 0,
      background: accent ? "var(--cs-blue-lt)" : "transparent",
      borderRadius: accent ? 4 : 0,
      margin: accent ? "2px -5px" : 0,
      padding: accent ? "4px 5px" : "3px 0",
    }}>
      <span style={{ fontSize: 12, color: "var(--text2)" }}>{label}</span>
      <span style={{ fontSize: 13, fontWeight: 700, fontFamily: "monospace", color: accent ? "var(--cs-blue)" : "var(--text)" }}>
        {value}{sub && <span style={{ fontSize: 10, fontWeight: 400, color: "var(--text3)" }}> {sub}</span>}
      </span>
    </div>
  );
}
function SectionHead({ title }) {
  return <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: "var(--text3)", marginBottom: 5 }}>{title}</div>;
}

// ── SharePoint Modal ──────────────────────────────────────────────────────────
function SPModal({ mode, onClose, onSave, onLoad, getToken }) {
  const [sites,    setSites]    = useState([]);
  const [drives,   setDrives]   = useState([]);
  const [folders,  setFolders]  = useState([]);
  const [files,    setFiles]    = useState([]);
  const [selSite,  setSelSite]  = useState(null);
  const [selDrive, setSelDrive] = useState(null);
  const [folderId, setFolderId] = useState(null);
  const [crumbs,   setCrumbs]   = useState([]);
  const [selFile,  setSelFile]  = useState(null);
  const [filename, setFilename] = useState("facility-estimate.json");
  const [loading,  setLoading]  = useState(false);
  const [error,    setError]    = useState(null);

  const gf = useCallback(async url => {
    const token = await getToken();
    const r = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
    if (!r.ok) throw new Error(`Graph ${r.status}`);
    return r.json();
  }, [getToken]);

  useEffect(() => {
    setLoading(true);
    gf("https://graph.microsoft.com/v1.0/sites?search=*&$top=20")
      .then(d => setSites(d.value || []))
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [gf]);

  const pickSite = async site => {
    setSelSite(site); setSelDrive(null); setFolders([]); setFiles([]); setCrumbs([]); setFolderId(null);
    setLoading(true);
    try { const d = await gf(`https://graph.microsoft.com/v1.0/sites/${site.id}/drives`); setDrives(d.value || []); }
    catch(e) { setError(e.message); } finally { setLoading(false); }
  };
  const pickDrive = async drive => {
    setSelDrive(drive); setFolderId(null); setCrumbs([{ id: null, name: drive.name }]);
    setLoading(true);
    try {
      const d = await gf(`https://graph.microsoft.com/v1.0/drives/${drive.id}/root/children`);
      setFolders((d.value || []).filter(i => i.folder));
      setFiles((d.value || []).filter(i => i.file && i.name.endsWith(".json")));
    } catch(e) { setError(e.message); } finally { setLoading(false); }
  };
  const openFolder = async folder => {
    setFolderId(folder.id); setCrumbs(prev => [...prev, { id: folder.id, name: folder.name }]);
    setLoading(true);
    try {
      const d = await gf(`https://graph.microsoft.com/v1.0/drives/${selDrive.id}/items/${folder.id}/children`);
      setFolders((d.value || []).filter(i => i.folder));
      setFiles((d.value || []).filter(i => i.file && i.name.endsWith(".json")));
    } catch(e) { setError(e.message); } finally { setLoading(false); }
  };

  const modalStyle = {
    position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)",
    display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000,
  };
  const dialogStyle = {
    background: "var(--surface)", border: "1px solid var(--cs-border)",
    borderRadius: "var(--radius-lg)", width: 480, maxWidth: "96vw",
    maxHeight: "80vh", display: "flex", flexDirection: "column", overflow: "hidden",
  };

  return (
    <div style={modalStyle} onClick={onClose}>
      <div style={dialogStyle} onClick={e => e.stopPropagation()}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "13px 17px", borderBottom: "1px solid var(--cs-border)", fontSize: 14, fontWeight: 700 }}>
          <span>{mode === "save" ? "Save to SharePoint" : "Load from SharePoint"}</span>
          <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 15, color: "var(--text2)" }}>✕</button>
        </div>
        {error   && <div style={{ background: "var(--red-lt)", color: "var(--red)", padding: "7px 17px", fontSize: 12 }}>{error}</div>}
        {loading && <div style={{ padding: "14px 17px", fontSize: 13, color: "var(--text2)" }}>Loading…</div>}

        <div style={{ padding: "13px 17px", overflowY: "auto", flex: 1 }}>
          {!selSite && !loading && (
            <>
              <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.1em", color: "var(--text3)", marginBottom: 7 }}>Select a SharePoint site</div>
              {sites.map(s => <button key={s.id} onClick={() => pickSite(s)} style={{ display: "block", width: "100%", textAlign: "left", background: "var(--bg)", border: "1px solid var(--cs-border)", borderRadius: "var(--radius)", padding: "7px 11px", fontSize: 13, cursor: "pointer", marginBottom: 3 }}>🏢 {s.displayName}</button>)}
            </>
          )}
          {selSite && !selDrive && !loading && (
            <>
              <button onClick={() => { setSelSite(null); setDrives([]); }} style={{ background: "none", border: "none", color: "var(--cs-blue)", fontSize: 13, cursor: "pointer", marginBottom: 9, padding: 0 }}>← Sites</button>
              <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.1em", color: "var(--text3)", marginBottom: 7 }}>Select a document library</div>
              {drives.map(d => <button key={d.id} onClick={() => pickDrive(d)} style={{ display: "block", width: "100%", textAlign: "left", background: "var(--bg)", border: "1px solid var(--cs-border)", borderRadius: "var(--radius)", padding: "7px 11px", fontSize: 13, cursor: "pointer", marginBottom: 3 }}>📁 {d.name}</button>)}
            </>
          )}
          {selDrive && (
            <>
              <div style={{ fontSize: 12, color: "var(--text2)", marginBottom: 9 }}>
                {crumbs.map((c, i) => (
                  <span key={i}>
                    <button onClick={async () => { setCrumbs(prev => prev.slice(0, i + 1)); if (!c.id) { pickDrive(selDrive); return; } setFolderId(c.id); setLoading(true); try { const d = await gf(`https://graph.microsoft.com/v1.0/drives/${selDrive.id}/items/${c.id}/children`); setFolders((d.value||[]).filter(i=>i.folder)); setFiles((d.value||[]).filter(i=>i.file&&i.name.endsWith(".json"))); } catch(e) { setError(e.message); } finally { setLoading(false); } }} style={{ background: "none", border: "none", color: "var(--cs-blue)", cursor: "pointer", fontSize: 12, padding: 0, textDecoration: "underline" }}>{c.name}</button>
                    {i < crumbs.length - 1 && <span style={{ color: "var(--text3)" }}> / </span>}
                  </span>
                ))}
              </div>
              {!loading && (
                <div style={{ maxHeight: 260, overflowY: "auto", marginBottom: 11 }}>
                  {folders.map(f => <button key={f.id} onClick={() => openFolder(f)} style={{ display: "block", width: "100%", textAlign: "left", background: "var(--bg)", border: "1px solid var(--cs-border)", borderRadius: "var(--radius)", padding: "7px 11px", fontSize: 13, cursor: "pointer", marginBottom: 3 }}>📂 {f.name}</button>)}
                  {mode === "load" && files.map(f => (
                    <button key={f.id} onClick={() => setSelFile(f)} style={{ display: "block", width: "100%", textAlign: "left", background: selFile?.id === f.id ? "var(--cs-blue-lt)" : "var(--bg)", border: selFile?.id === f.id ? "1px solid var(--cs-blue)" : "1px solid var(--cs-border)", borderRadius: "var(--radius)", padding: "7px 11px", fontSize: 13, cursor: "pointer", marginBottom: 3 }}>📄 {f.name}</button>
                  ))}
                  {folders.length === 0 && files.length === 0 && <div style={{ fontSize: 12, color: "var(--text3)", padding: "7px 0" }}>Empty folder</div>}
                </div>
              )}
              {mode === "save" && (
                <div style={{ display: "flex", gap: 8, alignItems: "center", borderTop: "1px solid var(--cs-border)", paddingTop: 11 }}>
                  <input type="text" value={filename} onChange={e => setFilename(e.target.value)} placeholder="filename.json" style={{ flex: 1 }} />
                  <button onClick={() => onSave(selDrive.id, folderId || "root", filename)} className="go-btn" style={{ width: "auto", padding: "7px 14px", marginTop: 0 }}>Save here</button>
                </div>
              )}
              {mode === "load" && (
                <div style={{ borderTop: "1px solid var(--cs-border)", paddingTop: 11 }}>
                  <button onClick={() => onLoad(selDrive.id, selFile.id)} disabled={!selFile} className="go-btn" style={{ marginTop: 0 }}>Load selected file</button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Room Row ──────────────────────────────────────────────────────────────────
function RoomRow({ room, index, showExhaust, onUpdate, onRemove, tunables }) {
  const rt   = ROOM_TYPES.find(t => t.value === room.type) || ROOM_TYPES[0];
  const calc = calcRoom(room, tunables);
  const setType = type => {
    const t = ROOM_TYPES.find(x => x.value === type);
    onUpdate({ type, ach: t?.achDefault ?? room.ach });
  };

  return (
    <div style={{ background: "var(--bg)", border: "1px solid var(--cs-border)", borderRadius: "var(--radius)", padding: "11px 13px", marginBottom: 10 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 9 }}>
        <span style={{ fontSize: 10, fontWeight: 700, color: "var(--text3)", minWidth: 18 }}>#{index + 1}</span>
        <input type="text" value={room.name} onChange={e => onUpdate({ name: e.target.value })}
          placeholder="Room name"
          style={{ flex: 1, maxWidth: 170, fontWeight: 600, border: "1px solid var(--cs-border2)", borderRadius: "var(--radius)", padding: "6px 8px", fontSize: 13 }} />
        <button onClick={onRemove} style={{ background: "none", border: "1px solid var(--red)", color: "var(--red)", borderRadius: "var(--radius)", padding: "3px 7px", fontSize: 11, cursor: "pointer" }}>✕</button>
      </div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 9, marginBottom: 9 }}>
        <SelField label="Type" value={room.type} onChange={setType} options={ROOM_TYPES.map(t => ({ value: t.value, label: t.label }))} />
        <NumField label="Length" unit="ft" value={room.length} min={1} onChange={v => onUpdate({ length: v })} />
        <NumField label="Width"  unit="ft" value={room.width}  min={1} onChange={v => onUpdate({ width: v })} />
        <NumField label="Height" unit="ft" value={room.height} min={1} onChange={v => onUpdate({ height: v })} />
        <NumField label="ACH" value={room.ach} min={1} max={600} hint={`${rt.achMin}–${rt.achMax}`} onChange={v => onUpdate({ ach: v })} />
        <NumField label="Spine Tap" unit="ft" value={room.spinePosition} min={0} hint="ft from AHU" onChange={v => onUpdate({ spinePosition: v })} />
        <NumField label="Branch/Drop" unit="ft" value={room.branchLength} min={1} onChange={v => onUpdate({ branchLength: v })} />
        <NumField label="Return Run" unit="ft" value={room.returnLength} min={1} onChange={v => onUpdate({ returnLength: v })} />
        {showExhaust && <NumField label="Exhaust Run" unit="ft" value={room.exhaustLength} min={1} onChange={v => onUpdate({ exhaustLength: v })} />}
      </div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 10, fontSize: 11, fontFamily: "monospace", color: "var(--cs-blue)", background: "var(--cs-blue-lt)", borderRadius: 4, padding: "5px 9px" }}>
        <span>Vol: {fmt(calc.vol)} ft³</span>
        <span>CFM: {fmt(calc.cfm, 0)}</span>
        <span>HEPAs: {calc.hepaCount}</span>
        <span>Branch: {calc.branchDia}"⌀</span>
        <span>Return: {calc.returnDia}"⌀</span>
        {showExhaust && <span>Exhaust: {calc.exhaustDia}"⌀</span>}
      </div>
    </div>
  );
}

// ── AHU Panel ─────────────────────────────────────────────────────────────────
function AhuPanel({ ahu, isOnly, onUpdate, onRemove, tunables }) {
  const ahuCalc = calcAhu(ahu, tunables);
  const isOA    = ahu.type === "oa100";
  const w       = calcWeights(ahu, ahuCalc, isOA, tunables);

  const updateRoom = (id, patch) => onUpdate({ rooms: ahu.rooms.map(r => r.id === id ? { ...r, ...patch } : r) });
  const removeRoom = id => onUpdate({ rooms: ahu.rooms.filter(r => r.id !== id) });
  const addRoom    = () => {
    const pos = ahu.rooms.length ? ahu.rooms[ahu.rooms.length - 1].spinePosition + 30 : 20;
    onUpdate({ rooms: [...ahu.rooms, defaultRoom(pos)] });
  };

  return (
    <div className="card" style={{ marginBottom: 18 }}>
      {/* AHU header */}
      <div style={{ background: "#FAFBFC", padding: "14px 18px", borderBottom: "1px solid var(--cs-border)" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
          <input type="text" value={ahu.name} onChange={e => onUpdate({ name: e.target.value })}
            style={{ fontSize: 16, fontWeight: 700, maxWidth: 200, border: "1px solid var(--cs-border2)", borderRadius: "var(--radius)", padding: "6px 8px" }} />
          {!isOnly && <button onClick={onRemove} style={{ background: "none", border: "1px solid var(--red)", color: "var(--red)", borderRadius: "var(--radius)", padding: "3px 10px", fontSize: 12, cursor: "pointer" }}>Remove AHU</button>}
        </div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
          <SelField label="AHU Type" value={ahu.type} onChange={v => onUpdate({ type: v })} options={AHU_TYPES.map(t => ({ value: t.value, label: t.label }))} />
          <SelField label="Duct Material" value={ahu.material} onChange={v => onUpdate({ material: v })} options={Object.entries(MATERIAL_LABELS).map(([k, v]) => ({ value: k, label: v }))} />
          <NumField label="Fittings" unit="%" value={ahu.fittingsPct} min={0} max={100} step={5} hint="Typical 20–30%" onChange={v => onUpdate({ fittingsPct: v })} />
          <NumField label="Return Spine" unit="ft" value={ahu.returnSpineLength} min={0} onChange={v => onUpdate({ returnSpineLength: v })} />
          {isOA && <NumField label="Exhaust Spine" unit="ft" value={ahu.exhaustSpineLength} min={0} onChange={v => onUpdate({ exhaustSpineLength: v })} />}
        </div>
      </div>

      {/* Spine diagram */}
      <div style={{ padding: "10px 18px", borderBottom: "1px solid var(--cs-border)", overflowX: "auto", background: "var(--bg)" }}>
        <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: "var(--text3)", marginBottom: 6 }}>Supply Spine — step-down sizing</div>
        <div style={{ display: "flex", alignItems: "flex-start", minWidth: 260 }}>
          <div style={{ background: "var(--cs-blue)", color: "#fff", fontSize: 10, fontWeight: 700, padding: "5px 7px", borderRadius: 4, whiteSpace: "nowrap", marginTop: 10 }}>AHU</div>
          <div style={{ display: "flex", flex: 1, alignItems: "flex-start" }}>
            {ahuCalc.spineSegments.map((seg, i) => (
              <div key={i} style={{ display: "flex", flexDirection: "column", minWidth: 36, flex: Math.max(seg.length, 10) }}>
                <div style={{ height: 18, background: "var(--surface)", border: "1px solid var(--cs-border)", borderLeft: "none", display: "flex", alignItems: "center", justifyContent: "center", marginTop: 10 }}>
                  <span style={{ fontSize: 9, fontFamily: "monospace", color: "var(--cs-blue)", whiteSpace: "nowrap" }}>{seg.dia}"</span>
                </div>
                <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end" }}>
                  <div style={{ width: 1, height: 10, background: "var(--text3)", marginRight: 5 }} />
                  <div style={{ fontSize: 9, color: "var(--text3)", whiteSpace: "nowrap", maxWidth: 65, overflow: "hidden", textOverflow: "ellipsis", marginRight: 3 }}>{ahuCalc.rooms[i]?.name || `R${i + 1}`}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Room list */}
      <div style={{ padding: "14px 18px" }}>
        {ahu.rooms.map((r, i) => (
          <RoomRow key={r.id} room={r} index={i} showExhaust={isOA} tunables={tunables}
            onUpdate={p => updateRoom(r.id, p)} onRemove={() => removeRoom(r.id)} />
        ))}
        <button onClick={addRoom} style={{ width: "100%", background: "none", border: "1px dashed var(--cs-border2)", color: "var(--text2)", borderRadius: "var(--radius)", padding: "7px 14px", fontSize: 13, cursor: "pointer" }}>
          + Add Room
        </button>
      </div>

      {/* AHU Summary */}
      <div style={{ background: "var(--bg)", borderTop: "1px solid var(--cs-border)", padding: "14px 18px" }}>
        <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: "var(--text3)", marginBottom: 10 }}>AHU Summary — {ahu.name}</div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))", gap: 14 }}>
          <div>
            <SectionHead title="Airflow" />
            <ResultRow label="Total Supply CFM" value={fmt(ahuCalc.totalCFM, 0)} sub="CFM" />
            <ResultRow label="Rooms Served" value={ahu.rooms.length} />
            <ResultRow label="Total HEPAs" value={ahuCalc.rooms.reduce((s, r) => s + r.hepaCount, 0)} sub="units" />
          </div>
          <div>
            <SectionHead title="Supply Weight" />
            <ResultRow label="Spine"   value={fmt(w.supplySpineLbs, 1)}  sub="lbs" indent />
            <ResultRow label="Branches" value={fmt(w.supplyBranchLbs, 1)} sub="lbs" indent />
            <ResultRow label="Supply Total" value={fmt(w.totalSupply, 1)} sub="lbs" accent />
          </div>
          <div>
            <SectionHead title="Return Weight" />
            <ResultRow label="Room Returns" value={fmt(w.returnRoomLbs, 1)} sub="lbs" indent />
            <ResultRow label={`Return Spine (${w.retSpineDia}"⌀)`} value={fmt(w.retSpineLbs, 1)} sub="lbs" indent />
            <ResultRow label="Return Total" value={fmt(w.totalReturn, 1)} sub="lbs" accent />
          </div>
          {isOA && (
            <div>
              <SectionHead title="Exhaust Weight" />
              <ResultRow label="Room Exhaust" value={fmt(w.exhaustRoomLbs, 1)} sub="lbs" indent />
              <ResultRow label={`Exhaust Spine (${w.exSpineDia}"⌀)`} value={fmt(w.exSpineLbs, 1)} sub="lbs" indent />
              <ResultRow label="Exhaust Total" value={fmt(w.totalExhaust, 1)} sub="lbs" accent />
            </div>
          )}
          <div style={{ borderTop: "1px solid var(--cs-border)", paddingTop: 8 }}>
            <SectionHead title="AHU Grand Total" />
            <ResultRow label="All Duct" value={fmt(w.grandTotal, 1)} sub="lbs" accent />
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Facility Summary ──────────────────────────────────────────────────────────
function FacilitySummary({ project, tunables }) {
  const rows = project.ahus.map(ahu => {
    const ac  = calcAhu(ahu, tunables);
    const isOA = ahu.type === "oa100";
    const w   = calcWeights(ahu, ac, isOA, tunables);
    return { ahu, ac, w, isOA };
  });
  const T = k => rows.reduce((s, r) => s + r.w[k], 0);

  return (
    <div className="card">
      <div className="card-head"><span className="card-head-icon">📊</span><span className="card-head-title">Facility Summary — {project.name}</span></div>
      <div className="card-body" style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
          <thead>
            <tr>
              {["AHU","Type","Rooms","CFM","HEPAs","Supply lbs","Return lbs","Exhaust lbs","Total lbs"].map(h => (
                <th key={h} style={{ textAlign: "left", fontSize: 10, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: "var(--text3)", padding: "5px 9px", borderBottom: "1px solid var(--cs-border)", whiteSpace: "nowrap" }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map(({ ahu, ac, w, isOA }) => (
              <tr key={ahu.id}>
                <td style={{ padding: "7px 9px", fontWeight: 600 }}>{ahu.name}</td>
                <td style={{ padding: "7px 9px", color: "var(--text2)" }}>{isOA ? "100% OA" : "Recirc"}</td>
                <td style={{ padding: "7px 9px", color: "var(--text2)" }}>{ahu.rooms.length}</td>
                <td style={{ padding: "7px 9px", fontFamily: "monospace" }}>{fmt(ac.totalCFM, 0)}</td>
                <td style={{ padding: "7px 9px", fontFamily: "monospace" }}>{ac.rooms.reduce((s, r) => s + r.hepaCount, 0)}</td>
                <td style={{ padding: "7px 9px", fontFamily: "monospace" }}>{fmt(w.totalSupply, 0)}</td>
                <td style={{ padding: "7px 9px", fontFamily: "monospace" }}>{fmt(w.totalReturn, 0)}</td>
                <td style={{ padding: "7px 9px", fontFamily: "monospace" }}>{isOA ? fmt(w.totalExhaust, 0) : "—"}</td>
                <td style={{ padding: "7px 9px", fontFamily: "monospace", fontWeight: 700, color: "var(--cs-blue)" }}>{fmt(w.grandTotal, 0)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr style={{ background: "var(--bg)" }}>
              <td colSpan={3} style={{ padding: 9, fontWeight: 700, fontSize: 10, letterSpacing: "0.08em", textTransform: "uppercase", color: "var(--text3)", borderTop: "2px solid var(--cs-border)" }}>FACILITY TOTAL</td>
              <td style={{ padding: 9, fontFamily: "monospace", fontWeight: 700, borderTop: "2px solid var(--cs-border)" }}>{fmt(rows.reduce((s, r) => s + r.ac.totalCFM, 0), 0)}</td>
              <td style={{ padding: 9, fontFamily: "monospace", fontWeight: 700, borderTop: "2px solid var(--cs-border)" }}>{rows.reduce((s, r) => s + r.ac.rooms.reduce((a, rm) => a + rm.hepaCount, 0), 0)}</td>
              <td style={{ padding: 9, fontFamily: "monospace", fontWeight: 700, borderTop: "2px solid var(--cs-border)" }}>{fmt(T("totalSupply"), 0)}</td>
              <td style={{ padding: 9, fontFamily: "monospace", fontWeight: 700, borderTop: "2px solid var(--cs-border)" }}>{fmt(T("totalReturn"), 0)}</td>
              <td style={{ padding: 9, fontFamily: "monospace", fontWeight: 700, borderTop: "2px solid var(--cs-border)" }}>{fmt(T("totalExhaust"), 0)}</td>
              <td style={{ padding: 9, fontFamily: "monospace", fontWeight: 700, fontSize: 15, color: "var(--cs-blue)", borderTop: "2px solid var(--cs-border)" }}>{fmt(T("grandTotal"), 0)}</td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}

// ── Toast ─────────────────────────────────────────────────────────────────────
function Toast({ msg, onClose }) {
  useState(() => { const t = setTimeout(onClose, 3500); return () => clearTimeout(t); });
  return (
    <div style={{ position: "fixed", bottom: 22, right: 22, background: "var(--cs-navy)", color: "#fff", border: "1px solid var(--cs-blue)", borderRadius: "var(--radius)", padding: "11px 17px", fontSize: 13, fontWeight: 600, zIndex: 2000, boxShadow: "0 4px 18px rgba(0,0,0,0.15)" }}>
      {msg}
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────
export default function DuctEstimator() {
  const { instance, accounts } = useMsal();
  const [project, setProject] = useState(defaultProject);
  const [tab,     setTab]     = useState("build");
  const [modal,   setModal]   = useState(null);
  const [toast,   setToast]   = useState(null);
  const [tuningRows, setTuningRows] = useState([]); // Takeoff Variables, Category='hvac_duct_sizing' — Setup > Basis of Design > Mechanical/HVAC

  // Design velocities are global (not project-scoped) — loaded once, same
  // convention as every other Basis of Design consumer (see RoomTakeoff.jsx's
  // identical comment on its own tunables fetch).
  useEffect(() => {
    sqlRead("EXEC sp_GetTakeoffVariables")
      .then(rows => setTuningRows((rows || []).filter(r => r.Category === "hvac_duct_sizing")))
      .catch(e => console.error("Takeoff variables load failed:", e));
  }, []);
  const tunables = {
    ...DEFAULT_DUCT_TUNABLES,
    ...Object.fromEntries(tuningRows.map(r => [r.VariableKey, +r.Value])),
  };

  const updProj = p => setProject(prev => ({ ...prev, ...p }));
  const updAhu  = (id, p) => setProject(prev => ({ ...prev, ahus: prev.ahus.map(a => a.id === id ? { ...a, ...p } : a) }));
  const remAhu  = id => setProject(prev => ({ ...prev, ahus: prev.ahus.filter(a => a.id !== id) }));
  const addAhu  = () => setProject(prev => ({ ...prev, ahus: [...prev.ahus, { ...defaultAhu(), name: `AHU-${prev.ahus.length + 1}`, id: uid() }] }));

  const getToken = async () => {
    const result = await instance.acquireTokenSilent({ scopes: loginScopes, account: accounts[0] });
    return result.accessToken;
  };

  const doSave = async (driveId, fId, fn) => {
    const token = await getToken();
    const url = fId === "root"
      ? `https://graph.microsoft.com/v1.0/drives/${driveId}/root:/${fn}:/content`
      : `https://graph.microsoft.com/v1.0/drives/${driveId}/items/${fId}:/${fn}:/content`;
    try {
      const r = await fetch(url, { method: "PUT", headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" }, body: JSON.stringify(project, null, 2) });
      if (!r.ok) throw new Error(`${r.status}`);
      setModal(null); setToast(`Saved "${fn}" to SharePoint`);
    } catch(e) { setToast("Save failed: " + e.message); }
  };

  const doLoad = async (driveId, fileId) => {
    const token = await getToken();
    try {
      const r = await fetch(`https://graph.microsoft.com/v1.0/drives/${driveId}/items/${fileId}/content`, { headers: { Authorization: `Bearer ${token}` } });
      if (!r.ok) throw new Error(`${r.status}`);
      setProject(await r.json()); setModal(null); setToast("Project loaded");
    } catch(e) { setToast("Load failed: " + e.message); }
  };

  const tabStyle = active => ({
    background: "none", border: "none", cursor: "pointer",
    fontSize: 13, fontWeight: 600, padding: "11px 16px",
    color: active ? "var(--cs-blue)" : "var(--text2)",
    borderBottom: active ? "2px solid var(--cs-blue)" : "2px solid transparent",
    transition: "color 0.15s",
  });

  return (
    <div>
      {/* Page header */}
      <div style={{ background: "var(--surface)", borderBottom: "1px solid var(--cs-border)", padding: "18px 24px", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
        <div>
          <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.16em", textTransform: "uppercase", color: "var(--cs-blue)", marginBottom: 3 }}>HVAC / Cleanroom</div>
          <h1 style={{ fontSize: 20, fontWeight: 700, letterSpacing: "-0.02em", color: "var(--text)" }}>Facility Duct &amp; HEPA Estimator</h1>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          <input type="text" value={project.name} onChange={e => updProj({ name: e.target.value })}
            style={{ width: 220, fontWeight: 600 }} />
          <button onClick={() => setModal("load")} style={{ background: "var(--bg)", border: "1px solid var(--cs-border2)", color: "var(--text)", borderRadius: "var(--radius)", padding: "7px 14px", fontSize: 13, cursor: "pointer" }}>📂 Load</button>
          <button onClick={() => setModal("save")} className="go-btn" style={{ width: "auto", padding: "7px 14px", marginTop: 0 }}>💾 Save</button>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: "flex", borderBottom: "1px solid var(--cs-border)", background: "var(--surface)", padding: "0 24px" }}>
        <button style={tabStyle(tab === "build")} onClick={() => setTab("build")}>Build</button>
        <button style={tabStyle(tab === "summary")} onClick={() => setTab("summary")}>Facility Summary</button>
      </div>

      {/* Content */}
      <div style={{ padding: "18px 24px 48px", maxWidth: 1100, margin: "0 auto" }}>
        {tab === "build" && (
          <div>
            {project.ahus.map(ahu => (
              <AhuPanel key={ahu.id} ahu={ahu} isOnly={project.ahus.length === 1} tunables={tunables}
                onUpdate={p => updAhu(ahu.id, p)} onRemove={() => remAhu(ahu.id)} />
            ))}
            <button onClick={addAhu} style={{ display: "block", width: "100%", background: "none", border: "1px dashed var(--cs-border2)", color: "var(--text2)", borderRadius: "var(--radius)", padding: 11, fontSize: 14, fontWeight: 600, cursor: "pointer" }}>
              + Add AHU
            </button>
          </div>
        )}
        {tab === "summary" && <FacilitySummary project={project} tunables={tunables} />}
      </div>

      {modal && <SPModal mode={modal} onClose={() => setModal(null)} onSave={doSave} onLoad={doLoad} getToken={getToken} />}
      {toast && <Toast msg={toast} onClose={() => setToast(null)} />}
    </div>
  );
}
